// client/src/components/pages/LexiStar/FleetTargets/AvailabilityView.tsx (ST-00247)
//
// Availability and utilisation per truck: the fleet's figures as tiles, a bar chart of the two
// percentages per truck, each truck's time as a segmented bar (Utilised / Idle / Delay / Down
// for maintenance / Breakdown in the shared asset status colours), the trend per day, week or
// month for the fleet or one truck, what every word means, and the numbers as a table (the
// charts' accessible twin). Figures from GET /fleet-availability; nothing is re-derived here.
//
// recharts 2 + React 19: axes, bars and lines are passed as arrays. With the react-is 18 recharts
// shipped with, a React 19 fragment's children were dropped; the react-is 19 override (ST-00260)
// fixed that repo-wide (see Replay/ReplayCharts.tsx).

import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ThemedSelect } from '../../../../types/commonComponents';
import { SectionTitle, cardClasses } from '../Dashboard/analyticsAtoms';
import type { FleetAvailabilityResponse, FleetGrain } from '../../../../types/lexistar-performance';
import {
  AVAILABLE_DEFINITION,
  FORMULAS,
  SERIES,
  STATE_META,
  STATE_ORDER,
  fleetSummary,
  fleetTiles,
  fmtHours,
  fmtPct,
  isTruckSort,
  trendOf,
  truckRows,
  type TruckRow,
  type TruckSort,
} from './fleetLogic';
import { AXIS, BAR_SIZE, CURSOR, GRID, RADIUS_H, TICK, TOOLTIP_LABEL, TOOLTIP_STYLE } from './chartTheme';

const pctTick = (v: number) => `${v}%`;

function Legend({ items }: { items: Array<{ label: string; dot: string; line?: boolean }> }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600" aria-label="Legend">
      {items.map((i) => (
        <li key={i.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`${i.line ? 'h-0.5 w-4' : 'h-2.5 w-2.5 rounded-sm'} inline-block ${i.dot}`} />
          {i.label}
        </li>
      ))}
    </ul>
  );
}

const SERIES_LEGEND = [
  { label: SERIES.availability.label, dot: SERIES.availability.dot },
  { label: SERIES.utilisation.label, dot: SERIES.utilisation.dot },
];

/** Availability and utilisation per truck, horizontal bars (truck names stay readable). */
function TruckBars({ rows }: { rows: TruckRow[] }) {
  const height = Math.max(120, rows.length * 44 + 36);
  return (
    <div className={`${cardClasses} p-4`}>
      <SectionTitle id="fleet-bars-title" aside={<Legend items={SERIES_LEGEND} />}>
        Availability and utilisation per truck
      </SectionTitle>
      <div role="img" aria-labelledby="fleet-bars-title" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 48, bottom: 0, left: 8 }} barGap={2} barCategoryGap={10}>
            {[
              <CartesianGrid key="grid" stroke={GRID} horizontal={false} />,
              <XAxis key="x" type="number" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={pctTick} tick={TICK} stroke={AXIS} />,
              <YAxis key="y" type="category" dataKey="label" width={104} tick={TICK} stroke={AXIS} interval={0} />,
              <Tooltip
                key="tip"
                cursor={CURSOR}
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL}
                formatter={(v, name) => [fmtPct(v === null || v === undefined ? null : Number(v)), name]}
                isAnimationActive={false}
              />,
              <Bar key="a" dataKey="availability" name={SERIES.availability.label} fill={SERIES.availability.color} barSize={BAR_SIZE} radius={RADIUS_H} isAnimationActive={false}>
                <LabelList dataKey="availability" position="right" formatter={(v: unknown) => fmtPct(v === null || v === undefined ? null : Number(v))} style={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              </Bar>,
              <Bar key="u" dataKey="utilisation" name={SERIES.utilisation.label} fill={SERIES.utilisation.color} barSize={BAR_SIZE} radius={RADIUS_H} isAnimationActive={false}>
                <LabelList dataKey="utilisation" position="right" formatter={(v: unknown) => fmtPct(v === null || v === undefined ? null : Number(v))} style={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              </Bar>,
            ]}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Each truck's counted time as one segmented bar (2 px surface gaps), in the state colours. */
function TimeSplit({ rows }: { rows: TruckRow[] }) {
  const legend = STATE_ORDER.map((s) => ({ label: STATE_META[s].label, dot: STATE_META[s].dot }));
  return (
    <div className={`${cardClasses} p-4`}>
      <SectionTitle id="fleet-split-title" aside={<Legend items={legend} />}>
        Where each truck’s time went
      </SectionTitle>
      <ul className="space-y-2.5" aria-labelledby="fleet-split-title">
        {rows.map((r) => {
          const words = STATE_ORDER.filter((s) => r.seconds[s] > 0)
            .map((s) => `${STATE_META[s].label} ${fmtHours(r.seconds[s])}`)
            .join(', ');
          return (
            <li key={r.id} className="grid grid-cols-[6.5rem_1fr] sm:grid-cols-[8rem_1fr_7rem] items-center gap-x-3 gap-y-1">
              <span className="text-xs font-medium text-slate-800 truncate" title={r.label}>
                {r.label}
              </span>
              <div
                className="flex h-3.5 w-full gap-[2px] overflow-hidden rounded bg-surface"
                role="img"
                aria-label={`${r.label}: ${words || 'no time counted'}`}
              >
                {STATE_ORDER.filter((s) => r.share[s] > 0).map((s) => (
                  <span
                    key={s}
                    className={`h-full first:rounded-l last:rounded-r ${STATE_META[s].dot}`}
                    style={{ width: `${(r.share[s] * 100).toFixed(3)}%` }}
                    title={`${STATE_META[s].label}: ${fmtHours(r.seconds[s])}`}
                  />
                ))}
              </div>
              <span className="hidden sm:block text-xs text-slate-600 tabular-nums text-right">
                {fmtPct(r.availability)} avail.
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The trend of the two percentages per period, the fleet's or one truck's. */
function Trend({ res, grain }: { res: FleetAvailabilityResponse; grain: FleetGrain }) {
  const [subject, setSubject] = useState<string>('fleet');
  const asset = subject === 'fleet' ? null : res.assets.find((a) => a.asset_id === subject) || null;
  const points = trendOf(asset ? asset.buckets : res.fleet.buckets, grain);
  const word = grain === 'day' ? 'day' : grain === 'week' ? 'week' : 'month';
  return (
    <div className={`${cardClasses} p-4`}>
      <SectionTitle
        id="fleet-trend-title"
        aside={
          <div className="flex flex-wrap items-center gap-3">
            <Legend items={SERIES_LEGEND.map((l) => ({ ...l, line: true }))} />
            <div className="w-44">
              <ThemedSelect aria-label="Trend for" value={subject} onChange={(e) => setSubject(String(e.target.value))}>
                <option value="fleet">Whole fleet</option>
                {res.assets.map((a) => (
                  <option key={a.asset_id} value={a.asset_id}>
                    {a.registration || a.fleet_no || a.asset_id}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </div>
        }
      >
        Trend per {word}
      </SectionTitle>
      {points.length < 2 ? (
        <p className="text-sm text-slate-600">Choose a longer period, or a shorter step, to see a trend.</p>
      ) : (
        <div role="img" aria-labelledby="fleet-trend-title" style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {[
                <CartesianGrid key="grid" stroke={GRID} vertical={false} />,
                <XAxis key="x" dataKey="tick" tick={TICK} stroke={AXIS} minTickGap={12} />,
                <YAxis key="y" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={pctTick} width={40} tick={TICK} stroke={AXIS} />,
                <Tooltip
                  key="tip"
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL}
                  labelFormatter={(_l, payload) => (payload && payload[0] ? String((payload[0].payload as { label: string }).label) : '')}
                  formatter={(v, name) => [fmtPct(v === null || v === undefined ? null : Number(v)), name]}
                  isAnimationActive={false}
                />,
                <Line key="a" type="linear" dataKey="availability" name={SERIES.availability.label} stroke={SERIES.availability.color} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)', fill: SERIES.availability.color }} connectNulls isAnimationActive={false} />,
                <Line key="u" type="linear" dataKey="utilisation" name={SERIES.utilisation.label} stroke={SERIES.utilisation.color} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)', fill: SERIES.utilisation.color }} connectNulls isAnimationActive={false} />,
              ]}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/** What every state means and how the two percentages are counted. */
export function Definitions() {
  return (
    <section className={`${cardClasses} p-4`} aria-labelledby="fleet-definitions-title">
      <SectionTitle id="fleet-definitions-title">How a truck’s time is counted</SectionTitle>
      <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2 text-sm" aria-labelledby="fleet-definitions-title">
        {STATE_ORDER.map((s) => (
          <div key={s} className="flex gap-2">
            <span aria-hidden="true" className={`mt-1.5 h-2.5 w-2.5 flex-none rounded-sm ${STATE_META[s].dot}`} />
            <div>
              <dt className="font-medium text-slate-900">{STATE_META[s].label}</dt>
              <dd className="text-slate-600">{STATE_META[s].definition}</dd>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <span aria-hidden="true" className="mt-1.5 flex h-2.5 w-2.5 flex-none overflow-hidden rounded-sm">
            <span className={`w-1/2 ${STATE_META.utilised.dot}`} />
            <span className={`w-1/2 ${STATE_META.idle.dot}`} />
          </span>
          <div>
            <dt className="font-medium text-slate-900">Available</dt>
            <dd className="text-slate-600">{AVAILABLE_DEFINITION}</dd>
          </div>
        </div>
      </dl>
      <ul className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-600">
        <li>{FORMULAS.availability}</li>
        <li>{FORMULAS.utilisation}</li>
        <li>{FORMULAS.precedence}</li>
        <li>{FORMULAS.counted}</li>
      </ul>
    </section>
  );
}

/** The numbers behind the charts. */
function TruckTable({ rows, sort, onSort }: { rows: TruckRow[]; sort: TruckSort; onSort: (s: TruckSort) => void }) {
  return (
    <div className={`${cardClasses} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
        <h2 id="fleet-table-title" className="text-sm font-semibold text-slate-900">
          Per truck
        </h2>
        <div className="w-48">
          <ThemedSelect aria-label="Sort trucks" value={sort} onChange={(e) => isTruckSort(e.target.value) && onSort(e.target.value)}>
            <option value="truck">By truck</option>
            <option value="availability">Lowest availability first</option>
            <option value="utilisation">Lowest utilisation first</option>
          </ThemedSelect>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" aria-labelledby="fleet-table-title">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th scope="col" className="px-4 py-2 font-medium">Truck</th>
              <th scope="col" className="px-3 py-2 font-medium text-right">Availability</th>
              <th scope="col" className="px-3 py-2 font-medium text-right">Utilisation</th>
              {STATE_ORDER.map((s) => (
                <th key={s} scope="col" className="px-3 py-2 font-medium text-right whitespace-nowrap">
                  {STATE_META[s].label}
                </th>
              ))}
              <th scope="col" className="px-4 py-2 font-medium text-right">Trips</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <th scope="row" className="px-4 py-2 text-left font-medium text-slate-900 whitespace-nowrap">
                  {r.label}
                </th>
                <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmtPct(r.availability)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-800">{fmtPct(r.utilisation)}</td>
                {STATE_ORDER.map((s) => (
                  <td key={s} className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {fmtHours(r.seconds[s])}
                  </td>
                ))}
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">{r.trips}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AvailabilityView({
  res,
  grain,
  sort,
  onSort,
}: {
  res: FleetAvailabilityResponse;
  grain: FleetGrain;
  sort: TruckSort;
  onSort: (s: TruckSort) => void;
}) {
  const rows = useMemo(() => truckRows(res, sort), [res, sort]);
  const tiles = fleetTiles(res);
  if (!res.assets.length) {
    return (
      <div className="space-y-4">
        <div className={`${cardClasses} p-6 text-center`}>
          <h2 className="text-base font-medium text-slate-900">No trucks yet</h2>
          <p className="mt-1 text-sm text-slate-600">Register your trucks under Settings → LexiStar → Assets to see their availability and utilisation.</p>
        </div>
        <Definitions />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700" aria-live="polite">
        {fleetSummary(res)}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <div key={t.id} className={`${cardClasses} p-3`}>
            <p className="text-xs font-medium text-slate-600">{t.label}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{t.value}</p>
            <p className="mt-0.5 text-xs text-slate-500">{t.hint}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <TruckBars rows={rows} />
        <TimeSplit rows={rows} />
      </div>
      <Trend res={res} grain={grain} />
      <Definitions />
      <TruckTable rows={rows} sort={sort} onSort={onSort} />
    </div>
  );
}
